/**
 * Production History Chart Component
 * Displays historical production estimations (producer vs auditor) by season
 * Includes both total kg and kg/ha metrics
 */

import { Card } from 'antd';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { HistoricalProductionData } from '../../services/api/productionDashboardService';

interface ProductionHistoryChartProps {
  data: HistoricalProductionData[];
}

export const ProductionHistoryChart: React.FC<ProductionHistoryChartProps> = ({ data }) => {
  // Format data for chart
  const chartData = data.map(item => ({
    name: item.season_name,
    'Est. Productor (kg)': Math.round(item.producer_estimation),
    'Est. Auditor (kg)': Math.round(item.auditor_estimation),
    'Productor (kg/ha)': item.producer_kg_per_ha,
    'Auditor (kg/ha)': item.auditor_kg_per_ha,
  }));

  // Custom tooltip formatter
  const formatTooltip = (value: number, name: string) => {
    if (name.includes('kg/ha')) {
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) + ' kg/ha';
    }
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ' kg';
  };

  return (
    <Card title="Evolución de Estimaciones por Campaña">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis
            yAxisId="left"
            tickFormatter={(value) =>
              new Intl.NumberFormat('es-ES', {
                notation: 'compact',
                compactDisplay: 'short',
              }).format(value)
            }
            label={{ value: 'Producción Total (kg)', angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={['auto', 'auto']}
            tickFormatter={(value) =>
              new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value)
            }
            label={{ value: 'kg/ha', angle: 90, position: 'insideRight' }}
          />
          <Tooltip formatter={formatTooltip} />
          <Legend />
          <Bar yAxisId="left" dataKey="Est. Productor (kg)" fill="#91d5ff" name="Est. Productor (kg)" />
          <Bar yAxisId="left" dataKey="Est. Auditor (kg)" fill="#52c41a" name="Est. Auditor (kg)" />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="Productor (kg/ha)"
            stroke="#1890ff"
            strokeWidth={3}
            name="Productor (kg/ha)"
            dot={{ r: 6, fill: '#1890ff', strokeWidth: 2, stroke: '#fff' }}
            strokeDasharray="5 5"
            activeDot={{ r: 8 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="Auditor (kg/ha)"
            stroke="#389e0d"
            strokeWidth={3}
            name="Auditor (kg/ha)"
            dot={{ r: 6, fill: '#389e0d', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 8 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
};
